import { markRaw } from 'vue'
import PaymentRequestRateColumn from './PaymentRequestRateColumn.vue'
import PaymentRequestUnitsColumn from './PaymentRequestUnitsColumn.vue'
import PaymentRequestRequestedAtColumn from './PaymentRequestRequestedAtColumn.vue'
import PaymentRequestPoNumberColumn from './PaymentRequestPoNumberColumn.vue'
import PaymentRequestTypeColumn from './PaymentRequestTypeColumn.vue'
import PaymentRequestDetailsColumn from './PaymentRequestDetailsColumn.vue'
import PaymentRequestBillingStartDateColumn from './PaymentRequestBillingStartDateColumn.vue'
import PaymentRequestBillingEndDateColumn from './PaymentRequestBillingEndDateColumn.vue'
import PaymentRequestInvoiceColumn from './PaymentRequestInvoiceColumn.vue'
import PaymentRequestTotalAmountColumn from './PaymentRequestTotalAmountColumn.vue'
import PaymentRequestWorkerPayoutStatusColumn from './PaymentRequestWorkerPayoutStatusColumn.vue'
import PaymentRequestStatusIdColumn from './PaymentRequestStatusIdColumn.vue'
import PaymentRequestWorkerColumn from './PaymentRequestWorkerColumn.vue'
import PaymentRequestHireColumn from './PaymentRequestHireColumn.vue'
import PaymentRequestHireOwnersColumn from './PaymentRequestHireOwnersColumn.vue'
import {
  PaymentRequestRateColumnClientFragmentDoc,
  PaymentRequestRateColumnClientFragment,
  PaymentRequestUnitsColumnClientFragmentDoc,
  PaymentRequestUnitsColumnClientFragment,
  PaymentRequestRequestedAtColumnFragmentDoc,
  PaymentRequestRequestedAtColumnFragment,
  PaymentRequestPoNumberColumnFragmentDoc,
  PaymentRequestPoNumberColumnFragment,
  PaymentRequestTypeColumnFragmentDoc,
  PaymentRequestTypeColumnFragment,
  PaymentRequestDetailsColumnFragmentDoc,
  PaymentRequestDetailsColumnFragment,
  PaymentRequestBillingStartDateColumnFragmentDoc,
  PaymentRequestBillingStartDateColumnFragment,
  PaymentRequestBillingEndDateColumnFragmentDoc,
  PaymentRequestBillingEndDateColumnFragment,
  PaymentRequestInvoiceColumnFragmentDoc,
  PaymentRequestInvoiceColumnFragment,
  PaymentRequestTotalAmountColumnFragmentDoc,
  PaymentRequestTotalAmountColumnFragment,
  PaymentRequestWorkerPayoutStatusColumnFragmentDoc,
  PaymentRequestWorkerPayoutStatusColumnFragment,
  PaymentRequestStatusIdColumnFragmentDoc,
  PaymentRequestStatusIdColumnFragment,
  PaymentRequestWorkerColumnClientFragmentDoc,
  PaymentRequestWorkerColumnClientFragment,
  PaymentRequestHireColumnClientFragmentDoc,
  PaymentRequestHireColumnClientFragment,
  PaymentRequestHireOwnersColumnFragmentDoc,
  PaymentRequestHireOwnersColumnFragment,
  PaymentRequestOrderByColumn,
  SortOrder,
} from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'

const ClientPaymentRequestColumns: ColumnMaps = {
  PaymentRequestRequestedAtColumn: {
    fragment: PaymentRequestRequestedAtColumnFragmentDoc,
    component: markRaw(PaymentRequestRequestedAtColumn),
    columnDef: {
      title: $t('Submitted'),
      key: 'requested_at',
      orderAble: true,
      renderUsing: {
        component: markRaw(PaymentRequestRequestedAtColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Submitted',
        extraction: (row: PaymentRequestRequestedAtColumnFragment) => row.issuedAt || '',
      },
    ],
    sort: [
      {
        name: `${PaymentRequestOrderByColumn.IssuedAt}_${SortOrder.Desc}`,
        orderByColumn: PaymentRequestOrderByColumn.IssuedAt,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Submitted on'),
        secondaryText: $t('Newest'),
      },
      {
        name: `${PaymentRequestOrderByColumn.IssuedAt}_${SortOrder.Asc}`,
        orderByColumn: PaymentRequestOrderByColumn.IssuedAt,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Submitted on'),
        secondaryText: $t('Oldest'),
      },
    ],
  },
  PaymentRequestStatusIdColumn: {
    fragment: PaymentRequestStatusIdColumnFragmentDoc,
    component: markRaw(PaymentRequestStatusIdColumn),
    columnDef: {
      title: $t('Status | ID'),
      key: 'status_id',
      orderAble: true,
      renderUsing: {
        component: markRaw(PaymentRequestStatusIdColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Status',
        extraction: (row: PaymentRequestStatusIdColumnFragment) => row.status,
      },
      {
        title: 'ID',
        extraction: (row: PaymentRequestStatusIdColumnFragment) => row.number,
      },
    ],
    sort: [
      {
        name: `${PaymentRequestOrderByColumn.Priority}_${SortOrder.Asc}`,
        orderByColumn: PaymentRequestOrderByColumn.Priority,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Priority'),
        secondaryText: $t('Pending first'),
      },
      {
        name: `${PaymentRequestOrderByColumn.Priority}_${SortOrder.Desc}`,
        orderByColumn: PaymentRequestOrderByColumn.Priority,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Priority'),
        secondaryText: $t('Pending last'),
      },
    ],
  },
  PaymentRequestWorkerColumnClient: {
    fragment: PaymentRequestWorkerColumnClientFragmentDoc,
    component: markRaw(PaymentRequestWorkerColumn),
    columnDef: {
      title: $t('Worker'),
      key: 'worker',
      orderAble: true,
      renderUsing: {
        component: markRaw(PaymentRequestWorkerColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker',
        extraction: (row: PaymentRequestWorkerColumnClientFragment) => row.worker.name,
      },
    ],
    sort: [
      {
        name: `${PaymentRequestOrderByColumn.Worker}_${SortOrder.Asc}`,
        orderByColumn: PaymentRequestOrderByColumn.Worker,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Ascending'),
      },
      {
        name: `${PaymentRequestOrderByColumn.Worker}_${SortOrder.Desc}`,
        orderByColumn: PaymentRequestOrderByColumn.Worker,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Descending'),
      },
    ],
  },
  PaymentRequestHireColumnClient: {
    fragment: PaymentRequestHireColumnClientFragmentDoc,
    component: markRaw(PaymentRequestHireColumn),
    columnDef: {
      title: $t('Hire'),
      key: 'hire',
      renderUsing: {
        component: markRaw(PaymentRequestHireColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Hire title',
        extraction: (row: PaymentRequestHireColumnClientFragment) =>
          row.hire?.activeContract?.jobName || '',
      },
    ],
  },
  PaymentRequestHireOwnersColumn: {
    fragment: PaymentRequestHireOwnersColumnFragmentDoc,
    component: markRaw(PaymentRequestHireOwnersColumn),
    columnDef: {
      title: $t('Hire owner(s)'),
      key: 'hire_owners',
      renderUsing: {
        component: markRaw(PaymentRequestHireOwnersColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Hire owner(s)',
        extraction: (row: PaymentRequestHireOwnersColumnFragment) =>
          row.hire?.owners?.map((owner) => owner.user.name).join(', ') || '',
      },
    ],
  },
  PaymentRequestPoNumberColumn: {
    fragment: PaymentRequestPoNumberColumnFragmentDoc,
    component: markRaw(PaymentRequestPoNumberColumn),
    columnDef: {
      title: $t('PO number'),
      key: 'po_number',
      renderUsing: {
        component: markRaw(PaymentRequestPoNumberColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'PO number',
        extraction: (row: PaymentRequestPoNumberColumnFragment) => row.purchaseOrderNumber || '',
      },
    ],
  },
  PaymentRequestTypeColumn: {
    fragment: PaymentRequestTypeColumnFragmentDoc,
    component: markRaw(PaymentRequestTypeColumn),
    columnDef: {
      title: $t('Type'),
      key: 'type',
      renderUsing: {
        component: markRaw(PaymentRequestTypeColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Type',
        extraction: (row: PaymentRequestTypeColumnFragment) => {
          const rateType = row.rateType?.toLowerCase()
          const isTimeBased = ['hourly', 'daily', 'weekly', 'monthly'].includes(rateType || '')
          const hasMilestone = (row.milestones?.data?.length ?? 0) > 0
          const hasBilledAmount = row.billedAmount > 0
          const hasExpenses = row.expensesAmount > 0

          const types: string[] = []
          if (hasMilestone) {
            types.push('Milestone')
          } else if (isTimeBased && hasBilledAmount) {
            types.push('Time')
          } else if (!isTimeBased && hasBilledAmount) {
            types.push('Amount')
          }
          if (hasExpenses) {
            types.push('Expenses')
          }
          return types.join(', ') || ''
        },
      },
    ],
  },
  PaymentRequestDetailsColumn: {
    fragment: PaymentRequestDetailsColumnFragmentDoc,
    component: markRaw(PaymentRequestDetailsColumn),
    columnDef: {
      title: $t('Details'),
      key: 'details',
      renderUsing: {
        component: markRaw(PaymentRequestDetailsColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Details',
        extraction: (row: PaymentRequestDetailsColumnFragment) => {
          const rateType = row.rateType?.toLowerCase()
          const isTimeBased = ['hourly', 'daily', 'weekly', 'monthly'].includes(rateType || '')
          const hasTimesheet = !!row.timesheet?.id || !!row.legacyTimesheet?.html
          return isTimeBased && hasTimesheet ? 'View timesheet' : ''
        },
      },
    ],
  },
  PaymentRequestBillingStartDateColumn: {
    fragment: PaymentRequestBillingStartDateColumnFragmentDoc,
    component: markRaw(PaymentRequestBillingStartDateColumn),
    columnDef: {
      title: $t('Billing start date'),
      key: 'billing_start_date',
      renderUsing: {
        component: markRaw(PaymentRequestBillingStartDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Billing start date',
        extraction: (row: PaymentRequestBillingStartDateColumnFragment) => row.startDate || '',
      },
    ],
  },
  PaymentRequestBillingEndDateColumn: {
    fragment: PaymentRequestBillingEndDateColumnFragmentDoc,
    component: markRaw(PaymentRequestBillingEndDateColumn),
    columnDef: {
      title: $t('Billing end date'),
      key: 'billing_end_date',
      renderUsing: {
        component: markRaw(PaymentRequestBillingEndDateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Billing end date',
        extraction: (row: PaymentRequestBillingEndDateColumnFragment) => row.endDate || '',
      },
    ],
  },
  PaymentRequestRateColumnClient: {
    fragment: PaymentRequestRateColumnClientFragmentDoc,
    component: markRaw(PaymentRequestRateColumn),
    columnDef: {
      title: $t('Rate'),
      key: 'rate',
      renderUsing: {
        component: markRaw(PaymentRequestRateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: PaymentRequestRateColumnClientFragment) => `${row.hire?.rate ?? ''}`,
      },
    ],
  },
  PaymentRequestUnitsColumnClient: {
    fragment: PaymentRequestUnitsColumnClientFragmentDoc,
    component: markRaw(PaymentRequestUnitsColumn),
    columnDef: {
      title: $t('Units'),
      key: 'units',
      renderUsing: {
        component: markRaw(PaymentRequestUnitsColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Units',
        extraction: (row: PaymentRequestUnitsColumnClientFragment) => {
          const rateType = row.rateType?.toLowerCase()
          const isTimeBased = ['hourly', 'daily', 'weekly', 'monthly'].includes(rateType || '')
          const hasBilledAmount = row.billedAmount > 0
          if (!isTimeBased || !hasBilledAmount) return ''
          return `${row.rateQuantity ?? ''}`
        },
      },
    ],
  },
  PaymentRequestWorkerPayoutStatusColumn: {
    fragment: PaymentRequestWorkerPayoutStatusColumnFragmentDoc,
    component: markRaw(PaymentRequestWorkerPayoutStatusColumn),
    columnDef: {
      title: $t('Worker payout'),
      key: 'worker_payout_status',
      renderUsing: {
        component: markRaw(PaymentRequestWorkerPayoutStatusColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker payout status',
        extraction: (row: PaymentRequestWorkerPayoutStatusColumnFragment) => row.workerPayoutStatus,
      },
    ],
  },
  PaymentRequestTotalAmountColumn: {
    fragment: PaymentRequestTotalAmountColumnFragmentDoc,
    component: markRaw(PaymentRequestTotalAmountColumn),
    columnDef: {
      title: $t('Total amount'),
      key: 'total_amount',
      orderAble: true,
      renderUsing: {
        component: markRaw(PaymentRequestTotalAmountColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Total amount',
        extraction: (row: PaymentRequestTotalAmountColumnFragment) =>
          `${row.currency} ${row.netAmount}`,
      },
    ],
    sort: [
      {
        name: `${PaymentRequestOrderByColumn.TotalAmount}_${SortOrder.Desc}`,
        orderByColumn: PaymentRequestOrderByColumn.TotalAmount,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Total amount'),
        secondaryText: $t('Highest first'),
      },
      {
        name: `${PaymentRequestOrderByColumn.TotalAmount}_${SortOrder.Asc}`,
        orderByColumn: PaymentRequestOrderByColumn.TotalAmount,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Total amount'),
        secondaryText: $t('Lowest first'),
      },
    ],
  },
  PaymentRequestInvoiceColumn: {
    fragment: PaymentRequestInvoiceColumnFragmentDoc,
    component: markRaw(PaymentRequestInvoiceColumn),
    columnDef: {
      title: $t('Invoice'),
      key: 'invoice',
      renderUsing: {
        component: markRaw(PaymentRequestInvoiceColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Invoice',
        extraction: (row: PaymentRequestInvoiceColumnFragment) =>
          row.invoice?.number ? `#${row.invoice.number}` : '',
      },
    ],
  },
}

export default ClientPaymentRequestColumns
